























import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import notificationService from "@/services/notificationService";
import Datagrid from "@/components/default/Datagrid.vue";
import moment from "moment";
import {
  CreateProjectReportCommand,
  OneNotePageListItem,
  IProjectReport,
  ProjectReport,
  ProjectReportsClient,
  UpdateProjectReportCommand,
} from "@/data/web-api-client";
import { DxLoadPanel } from "devextreme-vue/load-panel";
import TaskPopup from "@/modules/shared/tasks/components/TaskPopup.vue";
import { selectableEmployees } from "@/modules/employees/employeeSelection";

@Component({
  components: {
    Datagrid,
    DxLoadPanel,
    TaskPopup
  },
})
export default class ReportsTable extends Vue {
  @Prop({ required: true })
  projectId!: string;

  @Prop({ required: true })
  lastSyncDate!: Date;

  @Prop({ required: false, default: false })
  disabled!: boolean;

  client: ProjectReportsClient = new ProjectReportsClient(
    undefined,
    this.$axios
  );
  items: ProjectReport[] = [];
  templates: OneNotePageListItem[] = [];
  loading: boolean = false;
  saving: boolean = false;
  currentReport: any | null = null;
  employees = this.$store.getters["employees/items"] as any[];

  created() {
    this.loadData();
  }

  async loadData() {
    try {
      this.loading = true;

      var includeTemplates: boolean = true;
      var templatesLocalStorage = localStorage.getItem("templates");
      if (templatesLocalStorage != null) {
        includeTemplates = false;
      }
      const result = await this.client.getAll(this.projectId, includeTemplates);

      this.items = result.items!;
      this.templates = includeTemplates
        ? result.templates!
        : JSON.parse(templatesLocalStorage!);
      if (includeTemplates) {
        localStorage.setItem("templates", JSON.stringify(this.templates));
        this.lastSyncDate = new Date
      }
    } catch (err) {
      notificationService.showAxiosError(err);
    } finally {
      this.loading = false;
    }
  }

  update(e: any) {
    const self = this;
    e.cancel = new Promise(async (resolve, reject) => {
      try {
        if (self.projectId) {
          const updatedData: IProjectReport = Object.assign(
            {},
            e.oldData,
            e.newData
          );
          self.saving = true;
          await self.client.update(
            self.projectId,
            new UpdateProjectReportCommand({
              projectId: self.projectId,
              pageId: updatedData.pageId,
              type: updatedData.type,
              title: updatedData.title,
              date: updatedData.date,
              employeeId: updatedData.employeeId,
              completed: updatedData.completed,
            })
          );
          self.saving = false;
          // Manual add
          // self.GridInstance.cancelEditData();
          notificationService.saved();
        }

        resolve(false);
      } catch (err) {
        notificationService.failedToSave();
        reject(true);
      }
    });
  }

  deleteRow(e: { data: ProjectReport; cancel: any }) {
    const self = this;
    e.cancel = new Promise(async (resolve, reject) => {
      try {
        await self.client.delete(self.projectId, e.data.pageId!);
        notificationService.saved();

        resolve(false);
      } catch (err) {
        notificationService.failedToSave();
        resolve(true);
      }
    });
  }

  async createTask(e: any) {
    const report = e.row.data;

    this.currentReport = report;
    this.taskPopup.show();
  }

  get toolbarOptions() {
    const self = this;
    return [
      {
        location: "before",
        widget: "dxDropDownButton",
        visible: !self.disabled,
        options: {
          icon: "fas fa-plus",
          text: "Document",
          stylingMode: "outlined",
          type: "success",
          items: self.templates,
          displayExpr: "title",
          keyExpr: "id",
          onItemClick: async (e: { itemData: OneNotePageListItem }) => {
            const title = prompt("Geef de titel op:");
            if (title && title !== null) {
              try {
                self.saving = true;
                const result = await this.client.create(
                  self.projectId,
                  new CreateProjectReportCommand({
                    projectId: self.projectId,
                    templateId: e.itemData.id!,
                    type: e.itemData.type,
                    title,
                  })
                );
                self.items.push(result);
              } catch (err) {
                notificationService.showAxiosError(err);
              } finally {
                self.saving = false;
              }
            }
          },
        },
      },
      {
        location: "before",
        widget: "dxButton",
        options: {
          icon: "fas fa-sync-alt",
          text: "Sync",
          stylingMode: "outlined",
          onClick: async () => {
            try {
              self.saving = true;
              localStorage.removeItem("templates");
              self.loadData();
            } catch (err) {
              notificationService.showAxiosError(err);
            } finally {
              self.saving = false;
            }
          },
        },
      },
    ];
  }

  get GridInstance() {
    return ((this.$refs.grid as Vue).$refs.projectVisitsTable as any).instance;
  }

  get columns() {
    const self = this;
    return [
      {
        dataField: "employeeId",
        caption: "Medewerker",
        lookup: {
          // OS-214: the previous `visible: obj.accountEnabled` mapping did not actually filter
          // this DataGrid lookup - replaced with a real filter, sorted alphabetically, that
          // still keeps any employee already assigned to an existing report row selectable.
          dataSource: selectableEmployees(this.employees, {
            currentValue: this.items.map((item: any) => item.employeeId).filter(Boolean),
          }),
          displayExpr: "displayName",
          valueExpr: "id",
        },
      },
      {
        dataField: "template",
        caption: "Type",
      },
      {
        dataField: "title",
        caption: "Titel",
      },
      {
        dataField: "date",
        caption: "Datum",
        dataType: "date",
        format: "dd/MM/yyyy",
        allowEditing: false,
      },
      {
        dataField: "type",
        caption: "Soort",
        lookup: {
          dataSource: [
            {
              value: 0,
              text: "Verslag",
            },
            {
              value: 1,
              text: "Bezoek",
            },
          ],
          valueExpr: "value",
          displayExpr: "text",
        },
      },
      {
        dataField: "completed",
        caption: "Afgerond",
        dataType: "boolean",
      },
      {
        type: "buttons",
        buttons: [
          {
            hint: 'Taak maken',
            icon: 'fas fa-plus',
            visible(e: any) {
              return !e.row.isEditing && !e.row.data.isCompleted;
            },
            onClick(e: any) {
              self.createTask(e);
            }
          },
          {
            hint: "Open in webpagina",
            icon: "fas fa-globe",
            visible(e: any) {
              return !e.row.isEditing && !e.row.data.isCompleted;
            },
            onClick: (e: any) => {
              window.open(e.row.data.webUrl, "_blank");
            },
          },
          {
            hint: "Open in app",
            icon: "fas fa-clipboard",
            visible(e: any) {
              return !e.row.isEditing && !e.row.data.isCompleted;
            },
            onClick: (e: any) => {
              window.open(e.row.data.clientUrl, "_blank");
            },
          },
          "edit",
          "delete",
        ],
      },
    ];


  }

  convertDate(item: Date) {
    return moment(item).format("DD/MM/YYYY HH:mm");
  }

  get taskPopup() {
    return (this.$refs.TaskPopup as TaskPopup);
  }
}
